import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleBidsService } from 'src/app/services/bids/vehicle-bids.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { Bid, BidFilters, PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import Swal from 'sweetalert2';

interface FilterOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-my-bids',
  templateUrl: './my-bids.component.html',
  styleUrls: ['./my-bids.component.scss']
})
export class MyBidsComponent extends DestructorComponent implements OnInit {

  // Expose Math and parseInt to template
  Math = Math;
  parseInt = parseInt;

  // Data properties
  bids: Bid[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  currentPage: number = 1;
  showFiltersForm: boolean = false;

  // Selection properties
  selectedBids: string[] = [];

  // Filter properties
  filters: BidFilters = {
    bid_order_date_from: '',
    bid_order_date_to: '',
    auction_date_from: '',
    auction_date_to: '',
    auction_hall: '',
    created_by_id: '',
    bid_status: '',
    lot_no: '',
    keyword_search: ''
  };

  // Dropdown options
  auctionHallOptions: FilterOption[] = [];
  bidStatusOptions: FilterOption[] = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  constructor(
    private _vehicleBidsService: VehicleBidsService,
    private _generalService: GeneralService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['client_id']) {
        this.filters.created_by_id = params['client_id'];
      }
      this.loadBids();
    });

    this.loadAuctionHalls();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadBids(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    // Merge current filters with passed filters
    const apiFilters = { ...this.filters, ...filters };

    // Remove empty filters
    Object.keys(apiFilters).forEach(key => {
      if (!apiFilters[key as keyof BidFilters]) {
        delete (apiFilters as any)[key];
      }
    });

    this.subscriptions.push(
      this._vehicleBidsService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<Bid>) => {
          if (response.status && response.data) {
            this.bids = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.bids = [];
            this.resetPagination();
          }
          // Clear selection when new data is loaded
          this.selectedBids = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading bids:', error);
          this.bids = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  loadAuctionHalls(): void {
    // This would typically load from an API endpoint
    // For now, using static data as example
    this.auctionHallOptions = [
      { value: '', label: 'Select Auction Hall' },
      { value: 'jaa', label: 'JAA' },
      { value: 'uss', label: 'USS' },
      { value: 'jau', label: 'JAU' },
      { value: 'arai', label: 'ARAI' }
    ];
  }

  onPageChange(page: number): void {
    this.loadBids(page, this.filters.keyword_search, this.getFilterParams());
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.loadBids(1, this.filters.keyword_search, this.getFilterParams());
  }

  clearFilters(): void {
    this.filters = {
      bid_order_date_from: '',
      bid_order_date_to: '',
      auction_date_from: '',
      auction_date_to: '',
      created_by_id: '',
      auction_hall: '',
      bid_status: '',
      lot_no: '',
      keyword_search: ''
    };
    this.currentPage = 1;
    this.loadBids();
  }

  searchBids(): void {
    this.currentPage = 1;
    this.loadBids(1, this.filters.keyword_search, this.getFilterParams());
  }

  private getFilterParams(): Record<string, any> {
    const params: Record<string, any> = {};

    if (this.filters.bid_order_date_from) params['bid_order_date_from'] = this.filters.bid_order_date_from;
    if (this.filters.bid_order_date_to) params['bid_order_date_to'] = this.filters.bid_order_date_to;
    if (this.filters.auction_date_from) params['auction_date_from'] = this.filters.auction_date_from;
    if (this.filters.auction_date_to) params['auction_date_to'] = this.filters.auction_date_to;
    if (this.filters.auction_hall) params['auction_hall'] = this.filters.auction_hall;
    if (this.filters.bid_status) params['bid_status'] = this.filters.bid_status;
    if (this.filters.lot_no) params['lot_no'] = this.filters.lot_no;

    return params;
  }

  // Utility methods for template
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number, currency: string = 'JPY'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getStatusBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'badge-warning';
      case 'accepted':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      case 'cancelled':
        return 'badge-secondary';
      default:
        return 'badge-light';
    }
  }

  getMainImage(car: any): string {
    if (car.pictures && car.pictures.length > 0) {
      return car.pictures[0];
    }
    return 'assets/media/stock/600x400/img-1.jpg';
  }

  trackByFn(_index: number, bid: Bid): string {
    return bid.doc_id;
  }

  // Selection methods
  toggleSelection(bidId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedBids.includes(bidId)) {
        this.selectedBids.push(bidId);
      }
    } else {
      this.selectedBids = this.selectedBids.filter(id => id !== bidId);
    }
  }

  isSelected(bidId: string): boolean {
    return this.selectedBids.includes(bidId);
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      // Select all bids
      this.selectedBids = this.bids.map(bid => bid._id);
    } else {
      // Deselect all bids
      this.selectedBids = [];
    }
  }

  isAllSelected(): boolean {
    return this.bids.length > 0 && this.selectedBids.length === this.bids.length;
  }

  isIndeterminate(): boolean {
    return this.selectedBids.length > 0 && this.selectedBids.length < this.bids.length;
  }

  cancelAllSelectedBids(): void {
    if (this.selectedBids.length === 0) {
      return;
    }

    const bidCount = this.selectedBids.length;
    const bidText = bidCount === 1 ? 'bid' : 'bids';

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to cancel ${bidCount} ${bidText}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, cancel ${bidCount} ${bidText}!`,
      cancelButtonText: 'No, keep them',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-danger',
        cancelButton: 'btn fw-bold btn-active-light-primary'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // User confirmed - proceed with cancellation
        console.log('Selected bid IDs for cancellation:', this.selectedBids);


        this.subscriptions.push(
          this._vehicleBidsService.CancelBids(this.selectedBids).subscribe({
            next: () => {
              this.loadBids();
              this.selectedBids = [];
              Swal.fire('Cancelled!', 'Selected bids have been cancelled.', 'success');
            },
            error: (error) => {
              console.error('Error deleting bids:', error);
              Swal.fire('Error!', 'Failed to delete selected bids.', 'error');
            }
          })
        );
        // Here you would typically call an API to cancel the bids
        // For now, just logging the IDs as requested

        // Show success message

      }
    });
  }

  // Calculate active filters count
  get activeFiltersCount(): number {
    let count = 0;
    if (this.filters.bid_order_date_from) count++;
    if (this.filters.bid_order_date_to) count++;
    if (this.filters.auction_date_from) count++;
    if (this.filters.auction_date_to) count++;
    if (this.filters.auction_hall) count++;
    if (this.filters.bid_status) count++;
    if (this.filters.lot_no) count++;
    return count;
  }

  // Helper method to get unit display text
  getUnitDisplayText(bid: Bid): string {
    if(bid.bid_unit && !bid.bid_unit.number_of_units_bought){
      return `0 / ${bid.bid_unit.number_of_units}`;
    }

    if (bid.bid_unit && bid.bid_unit.number_of_units_bought !== undefined && bid.bid_unit.number_of_units !== undefined) {
      return `${bid.bid_unit.number_of_units_bought} / ${bid.bid_unit.number_of_units}`;
    }
    return 'N/A';
  }
}
